import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import { ProtectedRoute } from './components/auth'
import { ToastProvider } from './components/ui'
import { PageLoader } from './components/ui/PageLoader'
import { useAuthStore } from './store/auth'

// Lazy load all page components for code splitting
// This creates separate chunks that are loaded on demand
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Browse = lazy(() => import('./pages/Browse').then(m => ({ default: m.Browse })))
const WorkflowDetail = lazy(() => import('./pages/WorkflowDetail').then(m => ({ default: m.WorkflowDetail })))
const WorkflowEdit = lazy(() => import('./pages/WorkflowEdit').then(m => ({ default: m.WorkflowEdit })))
const WorkflowVersions = lazy(() => import('./pages/WorkflowVersions').then(m => ({ default: m.WorkflowVersions })))
const WorkflowForks = lazy(() => import('./pages/WorkflowForks').then(m => ({ default: m.WorkflowForks })))
const UserProfile = lazy(() => import('./pages/UserProfile').then(m => ({ default: m.UserProfile })))
const Search = lazy(() => import('./pages/Search').then(m => ({ default: m.Search })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })))
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })))
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })))

function App() {
  const { checkAuth, token } = useAuthStore()

  // Check authentication status on app load
  useEffect(() => {
    if (token) {
      checkAuth()
    }
  }, [token, checkAuth])

  return (
    <ToastProvider>
      <Suspense fallback={<PageLoader variant="full" />}>
        <Routes>
          {/* Auth routes - no layout */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Main app routes with layout */}
          <Route
            path="/*"
            element={
              <Layout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/browse/category/:category" element={<Browse />} />
                    <Route path="/workflow/:slug" element={<WorkflowDetail />} />
                    <Route
                      path="/workflow/:slug/edit"
                      element={
                        <ProtectedRoute>
                          <WorkflowEdit />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/workflow/:slug/versions" element={<WorkflowVersions />} />
                    <Route path="/workflow/:slug/forks" element={<WorkflowForks />} />
                    <Route path="/user/:id" element={<UserProfile />} />
                    <Route path="/search" element={<Search />} />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Layout>
            }
          />
        </Routes>
      </Suspense>
    </ToastProvider>
  )
}

export default App
